import React from 'react';
import { Printer } from 'lucide-react';
import Avatar from '../../parentPortal/components/Avatar';
import Button from '../../parentPortal/components/Button';
import { humanHours, formatMonthYear } from '../utils/format';

// `showIdentity=false` skips the avatar + name + card chrome. Use this when
// a StudentHeader (or equivalent) already displays the identity above this
// component — otherwise the name/avatar shows twice and the two cards read
// as a double header. In that mode the stat tiles float on the page
// background instead of nesting inside another bordered card.
export default function PortfolioHeader({ portfolio, onExport, showIdentity = true }) {
  const agg = portfolio?.aggregate || {};
  const first = formatMonthYear(agg.firstActivityAt);
  const last = formatMonthYear(agg.lastActivityAt);
  const dateRange = first && last
    ? `${first} – ${last}`
    : first
    ? `Since ${first}`
    : last
    ? `Through ${last}`
    : null;

  const stats = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 'var(--space-4)',
        marginTop: showIdentity ? 'var(--space-5)' : 0,
      }}
    >
      <Stat label="Courses completed" value={agg.totalCoursesCompleted ?? 0} />
      <Stat label="In progress" value={agg.totalCoursesInProgress ?? 0} />
      {/* Hide "Hours learned" and "Avg. mastery" until backend has real
          time-tracking and graded-work sources — they'd otherwise always
          render as 0 / "—", which reads worse than not showing them. */}
      {agg.totalMinutesLearned > 0 && (
        <Stat label="Hours learned" value={humanHours(agg.totalMinutesLearned)} />
      )}
      {agg.averageMasteryPercent != null && (
        <Stat label="Avg. mastery" value={`${agg.averageMasteryPercent}%`} />
      )}
    </div>
  );

  if (!showIdentity) {
    // Flat layout for the parent view. No card wrapper — the tiles read
    // as elevated pieces on the page background, and the compact header
    // bar sits above them as inline meta rather than a titled card.
    return (
      <div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            padding: 'var(--space-1) var(--space-1) var(--space-4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 'var(--text-label-size)',
                fontWeight: 700,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Transcript
            </span>
            {dateRange && (
              <span style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-secondary)' }}>{dateRange}</span>
            )}
          </div>
          {onExport && (
            <Button variant="secondary" size="sm" onClick={onExport}>
              <Printer size={14} />
              Export PDF
            </Button>
          )}
        </div>
        {stats}
      </div>
    );
  }

  // Standard learner-view: full card with avatar + name + stats stacked.
  return (
    <div
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-panel)',
        padding: 'var(--space-5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Avatar
          name={portfolio?.displayName || 'You'}
          color={portfolio?.avatarColor}
          photoUrl={portfolio?.photoUrl}
          size={56}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--text-label-size)',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 'var(--space-1)',
            }}
          >
            Transcript
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-page-size)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.01em',
            }}
          >
            {portfolio?.displayName || 'Your learning record'}
          </h1>
          {dateRange && (
            <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)' }}>
              {dateRange}
            </div>
          )}
        </div>
        {onExport && (
          <Button variant="secondary" size="sm" onClick={onExport}>
            <Printer size={14} />
            Export PDF
          </Button>
        )}
      </div>
      {stats}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-panel)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--text-section-size)',
          fontWeight: 800,
          color: 'var(--text-primary)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-label-size)',
          fontWeight: 600,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>
    </div>
  );
}
